import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material'
import { ReactElement, ReactNode } from 'react'
import styled from 'styled-components'
import { defaultTheme } from '../../../theme'
import { FilterAppliedCountChip } from './FilterAppliedCountChip'

interface FilterAccordionSectionProps {
  sectionKey: string
  title: ReactNode
  appliedCount?: number
  defaultExpanded?: boolean
  suppressTopBorder?: boolean
  variant?: 'sidebar' | 'drawer'
  children: ReactNode
}

export const FilterAccordionSection = ({
  sectionKey,
  title,
  appliedCount = 0,
  defaultExpanded = false,
  suppressTopBorder = false,
  variant = 'sidebar',
  children,
}: FilterAccordionSectionProps): ReactElement => (
  <StyledAccordion
    defaultExpanded={defaultExpanded}
    key={sectionKey}
    $suppressTopBorder={suppressTopBorder}
    $variant={variant}
  >
    <StyledAccordionSummary $variant={variant} expandIcon={<ExpandMoreIcon />}>
      <SummaryContent>
        <FilterTitle>{title}</FilterTitle>
        <FilterAppliedCountChip count={appliedCount} />
      </SummaryContent>
    </StyledAccordionSummary>
    <StyledAccordionDetails $variant={variant}>
      {children}
    </StyledAccordionDetails>
  </StyledAccordion>
)

const StyledAccordion = styled(Accordion)<{
  $suppressTopBorder: boolean
  $variant: 'sidebar' | 'drawer'
}>`
  width: 100%;
  border: 0;
  box-shadow: none;

  ${(p) =>
    p.$suppressTopBorder
      ? `
    &&::before {
      display: none;
    }
  `
      : ''}

  ${(p) =>
    p.$variant === 'drawer'
      ? `
    && {
      border-bottom: 1px solid ${defaultTheme.colors.border};
    }
    &&::before {
      display: none;
    }
  `
      : ''}
`

const StyledAccordionSummary = styled(AccordionSummary)<{
  $variant: 'sidebar' | 'drawer'
}>`
  && {
    padding: 0;
    min-height: 48px;
  }

  ${(p) =>
    p.$variant === 'drawer'
      ? `
    && {
      padding: 16px 0;
      min-height: 56px;
    }
  `
      : ''}
`

const StyledAccordionDetails = styled(AccordionDetails)<{
  $variant: 'sidebar' | 'drawer'
}>`
  && {
    padding: 0;
  }

  ${(p) =>
    p.$variant === 'drawer'
      ? `
    && {
      padding: 0 0 16px;
      overflow: hidden;
    }
  `
      : ''}
`

const SummaryContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const FilterTitle = styled.div`
  font-family: Roboto, serif;
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  color: ${defaultTheme.colors.text};
  flex: 1;
`
